import { MetricsState } from "../../logging";
import { useHttpRequest, LoadingState } from "../../http";
import { TeamAccessForUserResponseDto } from "../model/team-access-for-user-response.dto";
import { RequiredAxiosRequestConfig } from "../../http/axiosModel";

export interface UseWorkflowAuthorizationHookResult {
  loadingState: LoadingState;
  isAuthorized?: boolean;
}

/**
 * Custom react hook that abstracts the logic to check whether the current user is
 * authorized to make changes to a team.
 * @param {string} currentTeamId The id of the current team (this might be the parent team if the user is trying to
 * create a new team).
 * @returns {UseWorkflowAuthorizationHookResult} The status and result of the authorization check.  Whether or not the user
 * is authorized needs to be handled by the caller.
 */
function useWorkflowAuthorizationCheck(
  currentTeamId: string,
  metrics: MetricsState
): UseWorkflowAuthorizationHookResult {
  const requestConfig: RequiredAxiosRequestConfig = {
    url: `/api/v1/teams/teamAccess/me`,
    method: "POST",
    data: {
      teamIds: [currentTeamId],
    },
  };
  const {
    loadingState: isAuthorizedRequestLoadingState,
    data: isAuthorizedResponse,
  } = useHttpRequest<TeamAccessForUserResponseDto>(
    "workflowAuthorization",
    metrics,
    requestConfig
  );

  const hasClaims =
    isAuthorizedResponse &&
    isAuthorizedResponse.data &&
    isAuthorizedResponse.data.length > 0;
  return {
    loadingState: isAuthorizedRequestLoadingState,
    isAuthorized:
      hasClaims && isAuthorizedRequestLoadingState === LoadingState.RESOLVED,
  };
}

export default useWorkflowAuthorizationCheck;
